import React, { useEffect, useMemo, useRef, useState } from "react";

export default function CountryDropdown({
  options,
  value,
  name,
  placeholder = "Select a country",
  onChange,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value],
  );

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Keyboard support
  const onKeyDown = (e) => {
    if (
      !open &&
      (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")
    ) {
      e.preventDefault();
      setOpen(true);
      setActiveIndex(0);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[activeIndex]) {
        onChange({ target: { name, value: filtered[activeIndex].value } });
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <label className="block text-inputColor text-sm font-normal mb-1">
        Country
      </label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full flex justify-between text-left border border-inputBorder rounded-md px-3 py-2 h-[37px] focus:outline-none focus:ring-2 focus:ring-primary"
      >
        {selected ? (
          <span className="text-sm font-medium text-activeTabColor">
            {selected.label}
          </span>
        ) : (
          <span className="text-sm font-normal text-gray-400">
            {placeholder}
          </span>
        )}
        <span className="float-right inline-block translate-y-[2px]">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
              open ? "rotate-180" : "rotate-0"
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </span>
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg"
          role="listbox"
          tabIndex={-1}
          onKeyDown={onKeyDown}
        >
          <div className="p-2 border-b">
            <input
              autoFocus
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
              placeholder="Search…"
              className="w-full border border-inputBorder rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <ul className="max-h-64 overflow-auto">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-500">No results</li>
            )}
            {filtered.map((opt, idx) => {
              const isActive = idx === activeIndex;
              const isSelected = opt.value === value;
              return (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={isSelected}
                  className={`px-3 py-2 text-sm cursor-pointer ${
                    isActive ? "bg-indigo-50" : ""
                  } ${isSelected ? "font-medium" : ""}`}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onMouseDown={(e) => e.preventDefault()} // prevent blur before click
                  onClick={() => {
                    onChange({ target: { name, value: opt.value } });
                    setOpen(false);
                  }}
                >
                  {opt.label}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
